import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LANGUAGE_OPTIONS, useLanguage } from '../context/LanguageContext';
import './Navigation.css';

const Navigation = () => {
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [scrollTimeout, setScrollTimeout] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      if (currentScrollY < 10) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else if (currentScrollY < lastScrollY) {
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);

      const timeout = setTimeout(() => {
        setIsVisible(true);
      }, 150);

      setScrollTimeout(timeout);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [lastScrollY, scrollTimeout]);

  // Green SVG Icons
  const ForecastIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent-green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  );

  const PreMatchIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent-green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );

  const DailyCommentIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent-green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );

  const menuItems = [
    {
      path: '/posts',
      label: t('nav.comment'),
      lines: language === 'ka' ? ['დღის', 'კომენტარი'] : language === 'en' ? ['Daily', 'Comment'] : ['Комментарий', 'дня'],
      icon: <DailyCommentIcon />
    },
    {
      path: '/days-forecast',
      label: t('nav.forecast'),
      lines: language === 'ka' ? ['დღის', 'პროგნოზი'] : language === 'en' ? ['Daily', 'Forecast'] : ['Прогноз', 'дня'],
      icon: <ForecastIcon />
    },
    {
      path: '/winaswari-prognozi',
      label: t('nav.weekly'),
      lines: language === 'ka' ? ['კვირის', 'პროგნოზი'] : language === 'en' ? ['Weekly', 'Forecast'] : ['Недельный', 'прогноз'],
      icon: <PreMatchIcon />
    },
    { path: '/live', label: t('nav.live'), lines: ['LIVE'], icon: <span className="live-dot"></span> },
  ];

  return (
    <nav className={`navigation ${isVisible ? 'nav-visible' : 'nav-hidden'}`}>
      <div className="nav-container">
        <div className="nav-brand-row">
          <Link to="/about" className={`nav-logo ${location.pathname === '/about' ? 'active' : ''}`} aria-label="ჩვენ შესახებ">
            <img src="/ntflogo.png" alt="NTF Logo" className="logo-icon" />
          </Link>

          <div className="nav-language-row">
            <div className="language-switcher" role="group" aria-label={t('language.select')}>
              {LANGUAGE_OPTIONS.map(option => (
                <button
                  key={option.code}
                  type="button"
                  className={`language-option ${language === option.code ? 'active' : ''}`}
                  onClick={() => setLanguage(option.code)}
                  aria-label={option.label}
                  aria-pressed={language === option.code}
                  title={option.label}
                >
                  <span className="language-flag" aria-hidden="true">{option.flag}</span>
                </button>
              ))}
            </div>
          </div>

          <a
            className="flashscore-link"
            href="https://www.flashscore.com/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open Flashscore"
          >
            <img src="/flashscore-icon.png" alt="Flashscore" className="flashscore-icon" />
            <span className="flashscore-name">Flashscore</span>
          </a>
        </div>

        <div className="nav-menu">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
              aria-label={item.label}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-text" aria-hidden="true">
                {item.lines.map((line) => (
                  <span className="nav-text-line" key={line}>{line}</span>
                ))}
              </span>
            </Link>
          ))}
        </div>

      </div>
    </nav>
  );
};

export default Navigation;
